/**
 * Admin email configuration management
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
} from '@mui/material';
import {
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAdmin } from '../../contexts/AdminContext';
import { AdminEmailConfig, AdminEmailConfigCreateRequest } from '../../types/admin';
import { formatDate } from '../../utils/formatters';

const emptyForm: AdminEmailConfigCreateRequest = {
  email_type: 'support',
  email_address: '',
  is_active: true,
};

const EmailManagement: React.FC = () => {
  const {
    emailConfigs,
    emailConfigsLoading,
    emailConfigsError,
    loadEmailConfigs,
    createEmailConfig,
    updateEmailConfig,
    deleteEmailConfig,
    clearError,
  } = useAdmin();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminEmailConfig | null>(null);
  const [form, setForm] = useState<AdminEmailConfigCreateRequest>(emptyForm);
  const [smtpJson, setSmtpJson] = useState('{}');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadEmailConfigs();
  }, [loadEmailConfigs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadEmailConfigs();
    } finally {
      setRefreshing(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setSmtpJson('{}');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (config: AdminEmailConfig) => {
    setEditing(config);
    setForm({
      email_type: config.email_type,
      email_address: config.email_address,
      smtp_config: config.smtp_config,
      template_config: config.template_config,
      is_active: config.is_active,
    });
    setSmtpJson(JSON.stringify(config.smtp_config ?? {}, null, 2));
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    if (!form.email_address.trim()) {
      setFormError('Email address is required');
      return;
    }
    let smtp_config: Record<string, unknown> | undefined;
    try {
      smtp_config = smtpJson.trim() ? JSON.parse(smtpJson) : {};
    } catch {
      setFormError('SMTP config must be valid JSON');
      return;
    }
    try {
      if (editing) {
        await updateEmailConfig(editing.id, {
          email_address: form.email_address.trim(),
          smtp_config,
          is_active: form.is_active,
        });
      } else {
        await createEmailConfig({
          ...form,
          email_address: form.email_address.trim(),
          smtp_config,
        });
      }
      setDialogOpen(false);
    } catch {
      /* error surfaced via context */
    }
  };

  const handleDelete = async (config: AdminEmailConfig) => {
    if (!window.confirm(`Delete email config ${config.email_address}?`)) return;
    await deleteEmailConfig(config.id);
  };

  if (emailConfigsLoading && emailConfigs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" display="flex" alignItems="center" gap={1}>
          <EmailIcon />
          Email Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Email Config
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={refreshing} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {emailConfigsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
          {emailConfigsError}
        </Alert>
      )}

      <Card>
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Email address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {emailConfigs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      No email configurations yet. Add addresses for support, billing, alerts, etc.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                emailConfigs.map((config) => (
                  <TableRow key={config.id} hover>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{config.email_type}</TableCell>
                    <TableCell>{config.email_address}</TableCell>
                    <TableCell>
                      <Chip
                        label={config.is_active ? 'Active' : 'Inactive'}
                        color={config.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(config.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEdit(config)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(config)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Email Config' : 'New Email Config'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={!!editing}>
                <InputLabel>Email type</InputLabel>
                <Select
                  value={form.email_type}
                  label="Email type"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      email_type: e.target.value as AdminEmailConfigCreateRequest['email_type'],
                    }))
                  }
                >
                  <MenuItem value="support">Support</MenuItem>
                  <MenuItem value="alert">Alert</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="billing">Billing</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active ?? true}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email address"
                value={form.email_address}
                onChange={(e) => setForm((f) => ({ ...f, email_address: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="SMTP config (JSON, optional)"
                value={smtpJson}
                onChange={(e) => setSmtpJson(e.target.value)}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.875rem' } }}
                helperText="Host, port, credentials, etc. Leave {} if using platform default mailer."
              />
            </Grid>
            {formError && (
              <Grid item xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailManagement;
